import { useState, useEffect, useMemo } from 'react';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  FileText,
  MapPin,
  Users,
  Building2,
  Calendar,
  Wind,
  Wrench,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Download,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
} from 'lucide-react';
import { computeLift } from '@/utils/riggingCalculations.v2';
import type { AnchorVehicle, LiftDetail as EngineLift, LiftComputed, Scenario } from '@/types/planData';
import { ShownWork } from '@/components/rigging/ShownWork';
import { LiftDiagramPanel } from '@/components/rigging/LiftDiagramPanel';
import { BlockAttachmentInset } from '@/components/rigging/BlockAttachmentInset';
import { api } from '@/utils/api';
import { canCreatePlans } from '@/utils/roleGate';
import { createPlan, updatePlan, planApiErrorMessage } from '@/utils/planApi';
import {
  ClassificationWizard,
  defaultClassifierAnswers,
  type WizardLiftSnapshot,
} from '@/components/classification/ClassificationWizard';
import {
  ExceptionCard,
  type ConditionMeta,
  type ExceptionPath,
} from '@/components/classification/ExceptionCard';
import { ProofTestLog, type ProofTestRecord } from '@/components/classification/ProofTestLog';
import { LiftDetailCard, invalidateEquipmentCache } from '@/components/rigging/LiftDetailCard';
import { CONTAINER_TYPES, type EquipmentContainerType } from '@/components/rigging/equipmentPicker';
import { ComplianceSteps } from '@/components/classification/ComplianceSteps';
import { recomputeComputed, needsRecompute } from '@/utils/recomputeLift';
import { buildMergedComplianceSteps } from '@/utils/complianceStepsMerge';
import { readPlan, writePlan } from '@/utils/planDataCodec';
import { hydratePlanProjectFields, type PlanProjectOption } from '@/utils/planProjectPrefill';
import { planComplianceStatus } from '@/utils/planComplianceStatus';
import { exportPlanPdf, recomputePlanForExport } from '@/utils/pdfExport';
import { PersonnelPanel, type CrewMember } from '@/components/classification/PersonnelPanel';
import { AttachmentSlot } from '@/components/plan/AttachmentSlot';
import type { ClassificationResult, ComplianceStep, PlanDataV2, PlanAttachment, EquipmentInspectionSnapshotEntry } from '@/types/planData';
import type { ClassifierAnswers, ExceptionConditions } from '@/utils/classificationEngine';
import { inspectionDisplay, type UsabilityVerdict } from '@/components/rigging/equipmentPicker';
import type { Asset } from '@/types';

// ============ TYPES ============
// §4.4.5.1 plan-considerations checklist — short field labels, ids a–n.
const PLAN_CONSIDERATION_ITEMS: { id: string; label: string }[] = [
  { id: 'a', label: 'Site information' },
  { id: 'b', label: 'Structure location' },
  { id: 'c', label: 'Scope of work' },
  { id: 'd', label: 'Construction classification' },
  { id: 'e', label: 'Project personnel' },
  { id: 'f', label: 'Sequence & duration' },
  { id: 'g', label: 'Equipment incl. hoisting systems' },
  { id: 'h', label: 'Operational & non-operational loads + limitations' },
  { id: 'i', label: 'Special procedures for safe work environment' },
  { id: 'j', label: 'Climbing facilities type' },
  { id: 'k', label: 'Structure type/size/attachment-point details incl. obstructions' },
  { id: 'l', label: 'Rigging attachment details & locations' },
  { id: 'm', label: 'Gross loads' },
  { id: 'n', label: 'General site layout identifying hoist locations' },
];

interface ProjectInfo {
  projectName: string;
  projectNumber: string;
  date: string;
  completedBy: string;
  latitude: string;
  longitude: string;
  customer: string;
  structureOwner: string;
  siteAddress: string;
  siteName: string;
  siteIdNumber: string;
  structureType: 'guyed' | 'self-support' | 'monopole' | 'mast-pipe';
}

interface ProjectContact {
  role: string;
  name: string;
  company: string;
  phone: string;
}

interface StructureData {
  structureType: string;
  height: number;
  manufacturer: string;
  faceWidthAtBase: number;
  typicalSectionLength: number;
}

interface ConstructionDuration {
  mobilizationDate: string;
  demobilizationDate: string;
  maxOperationalWindSpeed: number;
  maxNonOperationalWindSpeed: number;
}

interface LiftSystemInput {
  liftName: string;
  riggingLoadLocation: 'inside' | 'outside';
  topSheaveLocation: 'tower' | 'gin-pole';
  loadPartConfiguration: 1 | 2 | 3 | 4;
  grossLoad: number;
  loadHeight: number;
  loadDistance: number;
  topBlockHeight: number;
  heelBlockHeight: number;
  heelBlockDistance: number;
  hoistDistance: number;
  tagDistance: number;
  tagMethod: 'straight' | 'selfTrolley' | 'staticTrolley';
  accountsForFriction: boolean;
  blockAttachment: '1-leg' | 'faceBasket2';
  faceWidthFt: number;
  slingLengthFt: number;
}

interface EquipmentItem {
  id: string;
  equipmentType: string;
  name: string;
  manufacturer: string;
  size: string;
  type: string;
  model: string;
  config: string;
  listedWLL: number;
  reducedWLL: number;
  requiredWLL: number;
}

// ============ COMPONENT ============
// Module-level components — MUST live outside the page component. Defining
// Section/NumInput inline gave them a new component identity on every render,
// so React remounted the whole subtree per keystroke (focus loss + scroll
// jumps + taps landing after remount). Stable identity = stable DOM/focus.

const Section = ({ id, title, icon: Icon, activeSection, onToggle, children }: {
  id: string; title: string; icon: any; activeSection: string; onToggle: (id: string) => void; children: React.ReactNode;
}) => {
  const isActive = activeSection === id;
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => onToggle(isActive ? '' : id)}
        className="w-full px-6 py-4 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-gray-900">{title}</span>
        </div>
        {isActive ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
      </button>
      {isActive && <div className="p-6 bg-white">{children}</div>}
    </div>
  );
};

// Number input that plays nice on mobile: holds the raw string while focused
// (clearing never injects a leading 0), commits parse on change, empty -> 0.
function NumInput({ value, onCommit, className, id }: {
  value: number; onCommit: (n: number) => void; className?: string; id?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (value === 0 ? '' : String(value));
  return (
    <input
      type="number"
      inputMode="decimal"
      id={id}
      value={shown}
      onFocus={() => setDraft(shown)}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const n = parseFloat(raw);
        if (!Number.isNaN(n)) onCommit(n);
        else if (raw.trim() === '') onCommit(0);
      }}
      onBlur={() => setDraft(null)}
      className={className}
    />
  );
}

export function CreateConstructionPlanPage() {
  const navigate = useNavigate();
  // Phase 2b route guard (ROLE-MATRIX-v1): crew tier cannot create plans —
  // bounce back to the list with a notice that says WHY and what to do,
  // never a silent redirect. Server enforcement stays in place regardless.
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    if (!canCreatePlans(stored?.role)) {
      navigate('/construction-plans', {
        replace: true,
        state: {
          notice:
            'Construction plans are created by foremen and above — crew members have view-only access. Ask your foreman or project manager if a plan needs to be created or updated.',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<string>('project-info');
  const [saving, setSaving] = useState(false);

  // B-3: real persistence — linked project, saved plan id, toast messages
  // DE-002: the plan-scoped project list carries the hydration fields
  // (address parts, carrier, coords, structure info) the form pre-fills from.
  const [projects, setProjects] = useState<PlanProjectOption[]>([]);
  const [linkedProjectId, setLinkedProjectId] = useState<number | null>(
    searchParams.get('projectId') ? Number(searchParams.get('projectId')) : null
  );
  const [savedPlanId, setSavedPlanId] = useState<number | null>(null);
  // D16 conformance (F-5 audit round 1): a signed plan disables the attachment
  // Upload/Delete controls client-side — the server already 409s them.
  const [planSignedAt, setPlanSignedAt] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  // C-2/C-3: classification wizard state (answers stored for audit in classification.inputs)
  const [classifierAnswers, setClassifierAnswers] = useState<ClassifierAnswers>(defaultClassifierAnswers());
  const [classification, setClassification] = useState<ClassificationResult | null>(null);
  // C-4: §4.4.4.1 exception cards + §9.6 proof-test log
  const [exceptionCardPath, setExceptionCardPath] = useState<ExceptionPath>('4.4.4.1(b)');
  const [exceptionConditions, setExceptionConditions] = useState<ExceptionConditions>({
    goodCondition: false,
    within4ft: false,
    max200lb: false,
    proofTest: false,
  });
  const [exceptionConditionsMeta, setExceptionConditionsMeta] = useState<Record<string, ConditionMeta>>({});
  const [complianceSteps, setComplianceSteps] = useState<ComplianceStep[]>([]);
  const [proofTests, setProofTests] = useState<ProofTestRecord[]>([]);
  const [crewMembers, setCrewMembers] = useState<{ id: number; firstName: string; lastName: string; role: string }[]>([]);
  // F-5: supporting documents (crane lift plan, structural/mount analyses) —
  // DISPLAY-ONLY client mirror of the server-owned planData.attachments list.
  // Never serialized back into the save payload (F-5 audit round 1: updatePlan
  // replaces planData wholesale, so writing the mirror from a stale tab would
  // clobber other tabs' entries); rebuilt from save/upload/delete responses.
  const [attachments, setAttachments] = useState<PlanAttachment[]>([]);
  // C-5: personnel (auto-populated from crew), stakeholders, JHA linkage
  const [personnel, setPersonnel] = useState<PlanDataV2['personnel']>({
    qualifiedPerson: null,
    competentRigger: null,
    onsiteCompetentPersons: [],
    qualifiedEngineer: null,
    autoPopulatedFrom: 'manual',
    populatedAt: new Date().toISOString(),
  });
  const [stakeholders, setStakeholders] = useState<PlanDataV2['stakeholders']>({});
  const [jhaReference, setJhaReference] = useState<string | null>(null);
  const [jhaOptions, setJhaOptions] = useState<{ id: number; title: string }[]>([]);
  // Form consolidation: 'Show math' toggle per lift (closed by default)
  const [showMath, setShowMath] = useState<Record<number, boolean>>({});
  // Form consolidation: §4.4.5.1 plan-considerations checklist (ids of checked items,
  // persisted in the plan payload as planConsiderations)
  const [planConsiderations, setPlanConsiderations] = useState<string[]>([]);
  const [showPlanConsiderations, setShowPlanConsiderations] = useState(false);

  useEffect(() => {
    // DE-002: plan-scoped project list (hydration fields for the create form)
    api
      .get('/construction-plans/projects')
      .then((response) => setProjects(response.data.data || []))
      .catch(() => setProjects([]));
    // C-5: crew members for personnel auto-population + proof-test inspector picker
    api
      .get('/construction-plans/crew')
      .then((response) => setCrewMembers(response.data.data || []))
      .catch(() => setCrewMembers([]));
  }, []);

  useEffect(() => {
    // C-5: JHA linkage picker options for the linked project
    if (!linkedProjectId) {
      setJhaOptions([]);
      return;
    }
    api
      .get(`/jha/project/${linkedProjectId}`)
      .then((response) => {
        const rows = response.data.data || [];
        setJhaOptions(
          rows.map((j: { id: number; title?: string; jobTask?: string }) => ({
            id: j.id,
            title: j.title ?? j.jobTask ?? `JHA ${j.id}`,
          }))
        );
      })
      .catch(() => setJhaOptions([]));
  }, [linkedProjectId]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  // DE-002 (UAT 2026-10-06): selecting a project hydrates the plan's Project
  // Information from the project record — name, customer/carrier, full site
  // address, coordinates, and the project's structure info. Only EMPTY fields
  // are filled; the user's own edits are never clobbered. siteName/siteId
  // stay blank (the project record carries no such fields — never invented).
  useEffect(() => {
    if (!linkedProjectId) {
      setStructureInfoNote(null);
      return;
    }
    const project = projects.find((p) => p.id === linkedProjectId);
    if (!project) return undefined;
    setProjectInfo((prev) => {
      const next = hydratePlanProjectFields(prev, project);
      setStructureInfoNote(next.structureInfoNote);
      return {
        ...prev,
        projectName: next.projectName,
        customer: next.customer,
        siteAddress: next.siteAddress,
        latitude: next.latitude,
        longitude: next.longitude,
        structureOwner: next.structureOwner,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedProjectId, projects]);

  // DE-006 (UAT 2026-10-06): a signed same-project JHA is auto-linked into the
  // plan — the plan creator pre-selects the project's JHA instead of leaving
  // the §1.6 rule-3 linkage as a manual re-association chore. Preference: a
  // signed JHA first (the compliance record), else the latest on the project.
  useEffect(() => {
    if (jhaReference || jhaOptions.length === 0) return;
    setJhaReference(`jha-${jhaOptions[0].id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jhaOptions]);

  // Project Information
  const [projectInfo, setProjectInfo] = useState<ProjectInfo>({
    projectName: '',
    projectNumber: '',
    date: new Date().toISOString().split('T')[0],
    completedBy: '',
    latitude: '',
    longitude: '',
    customer: '',
    structureOwner: '',
    siteAddress: '',
    siteName: '',
    siteIdNumber: '',
    structureType: 'guyed',
  });
  // DE-002: the project's §4.4.5 structure-info text, shown as a
  // project-sourced context line (never re-keyed by the user).
  const [structureInfoNote, setStructureInfoNote] = useState<string | null>(null);

  // Project Contacts
  const [contacts, setContacts] = useState<ProjectContact[]>([
    { role: 'Competent Rigger', name: '', company: '', phone: '' },
    { role: 'Qualified Person', name: '', company: '', phone: '' },
    { role: 'Prime GC PM', name: '', company: '', phone: '' },
    { role: 'Customer POC', name: '', company: '', phone: '' },
    { role: 'Structure Owner POC', name: '', company: '', phone: '' },
  ]);

  // Structure Data
  const [structureData, setStructureData] = useState<StructureData>({
    structureType: 'Guyed Tower',
    height: 0,
    manufacturer: '',
    faceWidthAtBase: 0,
    typicalSectionLength: 0,
  });

  // Construction Duration
  const [duration, setDuration] = useState<ConstructionDuration>({
    mobilizationDate: new Date().toISOString().split('T')[0],
    demobilizationDate: new Date().toISOString().split('T')[0],
    maxOperationalWindSpeed: 30,
    maxNonOperationalWindSpeed: 50,
  });

  // Scope & Sequencing
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [meansAndMethods, setMeansAndMethods] = useState('');
  const [constructionSequencing, setConstructionSequencing] = useState('');

  // Lift Systems
  const [liftSystems, setLiftSystems] = useState<LiftSystemInput[]>([{
    liftName: 'Primary Lift',
    riggingLoadLocation: 'outside',
    topSheaveLocation: 'tower',
    loadPartConfiguration: 2,
    grossLoad: 0,
    loadHeight: 0,
    loadDistance: 0,
    topBlockHeight: 0,
    heelBlockHeight: 0,
    heelBlockDistance: 0,
    hoistDistance: 0,
    tagDistance: 0,
    tagMethod: 'straight',
    accountsForFriction: false,
    blockAttachment: '1-leg',
    faceWidthFt: 0,
    slingLengthFt: 0,
  }]);

  // Equipment for each lift
  const [equipmentLists, setEquipmentLists] = useState<EquipmentItem[][]>([[
    { id: '1', equipmentType: 'Crown Block Sling', name: '', manufacturer: '', size: '7/8"', type: 'Synthetic Slings', model: '', config: 'Choker', listedWLL: 4200, reducedWLL: 4200, requiredWLL: 0 },
    { id: '2', equipmentType: 'Crown Block Shackle', name: '', manufacturer: '', size: '5/8"', type: 'Screw Pin', model: '', config: '', listedWLL: 7165, reducedWLL: 7165, requiredWLL: 0 },
    { id: '3', equipmentType: 'Crown Block', name: '', manufacturer: '', size: '', type: 'Steel Roller', model: '', config: '4-1/2" Diameter', listedWLL: 6000, reducedWLL: 6000, requiredWLL: 0 },
    { id: '4', equipmentType: 'Load Line', name: '', manufacturer: '', size: '1/2"', type: 'Synthetic Rope', model: 'Double Braid', config: 'Knot', listedWLL: 1100, reducedWLL: 1100, requiredWLL: 0 },
    { id: '5', equipmentType: 'Tag Line', name: '', manufacturer: '', size: '1/2"', type: 'Synthetic Rope', model: 'Double Braid', config: 'Knot', listedWLL: 1100, reducedWLL: 1100, requiredWLL: 0 },
    { id: '6', equipmentType: 'Heel Block Sling', name: '', manufacturer: '', size: '5/8"', type: 'Synthetic Slings', model: '', config: 'Choker', listedWLL: 2100, reducedWLL: 2100, requiredWLL: 0 },
    { id: '7', equipmentType: 'Heel Block Shackle', name: '', manufacturer: '', size: '5/8"', type: 'Screw Pin', model: '', config: '', listedWLL: 7165, reducedWLL: 7165, requiredWLL: 0 },
    { id: '8', equipmentType: 'Heel Block', name: '', manufacturer: '', size: '', type: 'Steel Roller', model: '', config: '4-1/2" Diameter', listedWLL: 6000, reducedWLL: 6000, requiredWLL: 0 },
    { id: '9', equipmentType: 'Capstan Hoist', name: 'AB Chance', manufacturer: 'AB Chance', size: '', type: 'Capstan', model: '', config: '', listedWLL: 1000, reducedWLL: 1000, requiredWLL: 0 },
    { id: '10', equipmentType: 'Hoist Anchorage (Truck)', name: '', manufacturer: '', size: '', type: 'Truck', model: '', config: 'Hitch Receiver', listedWLL: 7742, reducedWLL: 7742, requiredWLL: 0 },
  ]]);

  // Calculated forces for each lift
  const [calculatedForces, setCalculatedForces] = useState<(LiftComputed | null)[]>([null]);
  // E-5: per-lift v2 engine input (EngineLift) — LiftDiagramPanel rebuilds the
  // scene from this on every render; TowerDiagram v1 config state is retired.
  const [engineLifts, setEngineLifts] = useState<(EngineLift | null)[]>([null]);

  // F-1: full v2 LiftDetail cards — the real editing surface for scenario,
  // hardware (role-tagged equipment), DLF, complianceRefs, per-lift sign-off.
  // computed is NEVER trusted from these objects: recomputeComputed() runs on
  // every render/load (engineVersion mismatch forces recalc).
  const [liftDetails, setLiftDetails] = useState<EngineLift[]>([]);
  // S1: warn before unload while a plan is in progress and unsaved.
  const planDirty = !!projectInfo.projectName?.trim() || !!scopeOfWork.trim()
    || !!classification || complianceSteps.length > 0 || proofTests.length > 0
    || attachments.length > 0 || liftDetails.length > 0
    || personnel.qualifiedPerson !== null || personnel.onsiteCompetentPersons.length > 0;
  useUnsavedGuard(planDirty);

  // §8.8 #8: plan-header 'Equipment source: truck/trailer/crew' multi-select —
  // scopes the lift-detail equipment picker to gear assigned to those
  // containers (crews commonly pull from truck + trailer together).
  const [equipmentContainers, setEquipmentContainers] = useState<EquipmentContainerType[]>([]);

  // UAT-015: live inspection verdicts pushed up by each LiftDetailCard —
  // snapshot into the save payload so the SAVED plan keeps the inspection
  // status it knew at save time (point-in-time record, like anchorVehicle).
  const [equipmentVerdicts, setEquipmentVerdicts] = useState<
    Record<string, { display: 'current' | 'periodic-due' | 'tagged-out' | 'never-inspected' | 'unknown'; reason: string | null; at: string }>
  >({});
  const handleVerdictsLoaded = (
    assets: Asset[],
    verdicts: Record<string, UsabilityVerdict>
  ) => {
    setEquipmentVerdicts((prev) => {
      const next = { ...prev };
      for (const a of assets) {
        const v = verdicts[String(a.id)];
        if (!v) continue;
        next[String(a.id)] = {
          display: inspectionDisplay(v, a),
          reason: v.reason,
          at: new Date().toISOString(),
        };
      }
      return next;
    });
  };

  // WD-T6 / v1.4 #1 — plan ANCHOR TRUCK picker. The truck is the anchor the
  // capstan winch attaches to; the choice persists in planData.anchorVehicle
  // (save-path, SERVER-RESOLVED from the real vehicle row) and drives the
  // truck-anchor utilization panel in every lift's rigging details.
  // SOL-WDT6-003 fix: fetch from the PLAN-scoped anchor-vehicles endpoint
  // (company VEHICLE list for plan authors) — the asset-tracker endpoint is
  // scoped to own-held + crew assets and wrongly hides other crews' trucks
  // from a company-wide plan author.
  const [anchorVehicle, setAnchorVehicle] = useState<AnchorVehicle | null>(null);
  // DE-003: rows carry a source ('ASSET' = asset tracker, 'FLEET' = Fleet
  // module DotVehicle) so one picker serves both without duplicate records.
  const [vehicleAssets, setVehicleAssets] = useState<Array<{ id: number; name: string; weightLbs?: number | string | null; anchorCapacityLbs?: number | string | null; source?: 'ASSET' | 'FLEET'; fleetVehicleId?: number | null }>>([]);
  const [vehicleLoadState, setVehicleLoadState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  useEffect(() => {
    let cancelled = false;
    api
      .get('/construction-plans/anchor-vehicles')
      .then((res: any) => {
        if (cancelled) return;
        setVehicleAssets(res.data?.data ?? []);
        setVehicleLoadState('loaded');
      })
      .catch(() => {
        if (cancelled) return;
        setVehicleLoadState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const toNum = (v: number | string | null | undefined): number | null => {
    const n = typeof v === 'string' ? parseFloat(v) : v;
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null;
  };

  // DE-003: stable option keys across both vehicle sources (ids can collide
  // between the asset tracker and the Fleet module, so keys are namespaced).
  const vehicleRowKey = (v: { id: number; source?: 'ASSET' | 'FLEET'; fleetVehicleId?: number | null }): string =>
    v.source === 'FLEET' ? `fleet:${v.fleetVehicleId ?? v.id}` : `asset:${v.id}`;
  const anchorVehicleKey = (a: AnchorVehicle): string =>
    a.source === 'FLEET' ? `fleet:${a.fleetVehicleId ?? ''}` : `asset:${a.assetId ?? ''}`;

  const syncLiftDetailsFromSystems = (systems: LiftSystemInput[]) => {
    setLiftDetails((prev) =>
      systems.map((lift, i) => {
        const base = buildEngineLiftFor(lift, i, projectInfo.structureType);
        const existing = prev[i];
        if (!existing || needsRecompute(existing)) return base;
        // keep card-level edits (scenario/refs/sign-off/equipment) but refresh
        // geometry fields the creator form owns — computed recomputed on render.
        return {
          ...base,
          scenario: existing.scenario,
          personnelLift: existing.personnelLift,
          hardware: { ...base.hardware, equipment: existing.hardware.equipment },
          loading: { ...base.loading, dynamicFactor: existing.loading.dynamicFactor },
          complianceRefs: existing.complianceRefs,
          signOff: existing.signOff,
        };
      })
    );
  };

  // Back-sync LiftDetail card edits into the v1 liftSystems fields the
  // classification snapshots and buildPlanPayload still consume.
  const updateLiftDetail = (index: number, next: EngineLift) => {
    setLiftDetails((prev) => prev.map((l, i) => (i === index ? next : l)));
    const updated = [...liftSystems];
    const sys = { ...updated[index] };
    sys.liftName = next.name;
    sys.grossLoad = next.loading.grossLoadLbs;
    sys.loadHeight = next.geometry.load.heightFt;
    sys.loadDistance = next.geometry.load.distanceFromTowerFt;
    sys.topBlockHeight = next.geometry.topBlock.heightFt;
    sys.heelBlockHeight = next.geometry.heelBlock?.heightFt ?? 0;
    sys.heelBlockDistance = next.geometry.heelBlock?.offsetFromTowerFt ?? 0;
    sys.hoistDistance = next.geometry.winch.distanceFromTowerFt;
    sys.tagDistance = next.geometry.tag.anchorDistanceFt ?? sys.tagDistance;
    sys.loadPartConfiguration = next.hardware.blockParts;
    sys.accountsForFriction = next.loading.accountsForFriction;
    setLiftSystems(updated);
    syncLiftDetailsFromSystemsOnEdit(index, next, updated);
  };

  // After a card edit, refresh engineLifts + calculatedForces from the edited card.
  const syncLiftDetailsFromSystemsOnEdit = (index: number, next: EngineLift, systems: LiftSystemInput[]) => {
    const computedResult = recomputeComputed(next);
    const newForces = [...calculatedForces];
    newForces[index] = computedResult;
    setCalculatedForces(newForces);
    const newEngineLifts = [...engineLifts];
    newEngineLifts[index] = next;
    setEngineLifts(newEngineLifts);
    // keep wizard snapshots sourced from liftSystems in sync
    void systems;
  };

  // Build the v2 engine input from the raw lift inputs (pure — no state writes).
  // Angles are DERIVED from distances inside the engine (D9 — no angle inputs).
  const buildEngineLift = (lift: LiftSystemInput, liftIndex: number): EngineLift =>
    buildEngineLiftFor(lift, liftIndex, projectInfo.structureType);

  const buildEngineLiftFor = (lift: LiftSystemInput, liftIndex: number, structureTypeRaw: string): EngineLift => {
    const structureType = (['monopole', 'self-support', 'guyed', 'mast-pipe'] as const)
      .includes(structureTypeRaw as never)
      ? (structureTypeRaw as EngineLift['geometry']['structureType'])
      : 'guyed';
    const useHeel = lift.heelBlockHeight > 0;
    return {
      id: `lift-${liftIndex + 1}`,
      name: lift.liftName,
      scenario: useHeel ? 'S2-top-heel' : 'S1-top-only',
      personnelLift: false,
      geometry: {
        structureType,
        structureHeightFt: lift.topBlockHeight,
        topBlock: {
          heightFt: lift.topBlockHeight,
          offsetFromCenterFt: 0,
          attachment: lift.blockAttachment === 'faceBasket2'
            ? {
                method: 'faceBasket2',
                faceWidthFt: lift.faceWidthFt > 0 ? lift.faceWidthFt : undefined,
                slingLengthFt: lift.slingLengthFt > 0 ? lift.slingLengthFt : undefined,
                slingEnds: ['legPanelPoint', 'legPanelPoint'],
              }
            : { method: 'legPanelPoint' },
        },
        heelBlock: useHeel
          ? { heightFt: lift.heelBlockHeight, offsetFromTowerFt: lift.heelBlockDistance, attachment: { method: 'poleShaft' } }
          : null,
        jumpBlock: null,
        redirectBlock: null,
        mountBlock: null,
        tieBackAnchor: null,
        highline: null,
        highestAttachmentFt: lift.topBlockHeight,
        winch: { distanceFromTowerFt: lift.hoistDistance, heightFt: 0, side: 'A', type: 'capstan' },
        load: { heightFt: lift.loadHeight, distanceFromTowerFt: lift.loadDistance },
        tag: { method: lift.tagMethod ?? 'straight', anchorDistanceFt: lift.tagDistance, anchorHeightFt: 0 },
      },
      hardware: {
        blockParts: lift.loadPartConfiguration,
        blockAttachment: lift.blockAttachment === 'faceBasket2' ? '2-leg' : '1-leg',
        slingHitch: 'basket',
        ropeType: 'synthetic',
        ropeDiameterIn: 0.5,
        sheaveCount: lift.loadPartConfiguration,
        sheaveBearing: 'plain',
        frictionFactorPerSheave: lift.accountsForFriction ? 0 : 1, // 0 ⇒ A-13(i) table; 1.0 ⇒ ideal
        equipment: [],
      },
      loading: {
        grossLoadLbs: lift.grossLoad,
        dynamicFactor: 1.0,
        accountsForFriction: lift.accountsForFriction,
      },
      computed: null as never, // re-derived on every calculation — never trusted from storage
      complianceRefs: ['A10.48-2023 §4.4.4.1(b)'],
      signOff: { reviewedBy: null, at: null },
    };
  };

  // Calculate forces for a lift — D-6 CUTOVER: v2 engine (computeLift).
  // Refused configurations emit NO numeric forces — the results panel shows the
  // red-hatched refusal block instead (via ShownWork).
  const calculateLiftForces = (liftIndex: number) => {
    const lift = liftSystems[liftIndex];
    const engineLift = buildEngineLift(lift, liftIndex);

    let computedResult: LiftComputed | null = null;
    try {
      computedResult = computeLift(engineLift);
    } catch {
      computedResult = null;
    }

    // Equipment required WLL — computed forces straight from the v2 engine
    // (NO SF multiplier — Z9: SF is baked into the manufacturer WLL).
    const updatedEquipment = [...equipmentLists[liftIndex]];
    if (computedResult && computedResult.status === 'ok') {
      const f = computedResult.forces;
      updatedEquipment.forEach((eq) => {
        if (eq.equipmentType.includes('Crown Block') || eq.equipmentType.includes('Crown Block Sling') || eq.equipmentType.includes('Crown Block Shackle')) {
          eq.requiredWLL = f.topBlockResultantLbs;
        } else if (eq.equipmentType.includes('Load Line')) {
          eq.requiredWLL = f.linePartTensionLbs;
        } else if (eq.equipmentType.includes('Tag Line')) {
          eq.requiredWLL = f.tagLineLbs ?? 0;
        } else if (eq.equipmentType.includes('Heel Block Sling') || eq.equipmentType.includes('Heel Block Shackle')) {
          eq.requiredWLL = f.heelBlockLbs ?? 0;
        } else if (eq.equipmentType === 'Heel Block') {
          eq.requiredWLL = f.heelBlockLbs ?? 0;
        } else if (eq.equipmentType.includes('Capstan') || eq.equipmentType.includes('Hoist')) {
          eq.requiredWLL = f.winchPullLbs;
        } else if (eq.equipmentType.includes('Truck') || eq.equipmentType.includes('Anchorage')) {
          eq.requiredWLL = f.winchPullLbs * 10.2; // Anchorage multiplier
        }
      });
    } else {
      // Refused — no numeric forces; zero out the required-WLL column (the
      // refusal block replaces the force panel; utilization is N/A).
      updatedEquipment.forEach((eq) => { eq.requiredWLL = 0; });
    }
    const newEquipmentLists = [...equipmentLists];
    newEquipmentLists[liftIndex] = updatedEquipment;
    setEquipmentLists(newEquipmentLists);

    const newForces = [...calculatedForces];
    newForces[liftIndex] = computedResult;
    setCalculatedForces(newForces);

    // E-5: store the engine input for the LiftDiagramPanel (v2 diagram)
    const newEngineLifts = [...engineLifts];
    newEngineLifts[liftIndex] = engineLift;
    setEngineLifts(newEngineLifts);
  };

  // Utilization band per Z9-resolved D12: required ÷ WLL, 1:1 — NO SF
  // multiplier (SF is baked into the manufacturer WLL). Red >100% (R-5
  // refusal), amber ≥80% advisory band.
  const getUtilizationStatus = (required: number, available: number) => {
    if (available === 0) return { percent: 0, status: 'N/A', color: 'text-gray-500' };
    const percent = (required / available) * 100;
    if (percent > 100) return { percent, status: 'FAIL', color: 'text-red-600' };
    if (percent >= 80) return { percent, status: 'PASS', color: 'text-amber-600' };
    return { percent, status: 'PASS', color: 'text-green-600' };
  };

  // Form consolidation: computed-force-vs-WLL check — FAIL when force > WLL,
  // WARN when force ≥ 80% of WLL, OK otherwise.
  const getWllStatus = (force: number, wll: number): 'FAIL' | 'WARN' | 'OK' | null => {
    if (force <= 0 || wll <= 0) return null;
    if (force > wll) return 'FAIL';
    if (force >= 0.8 * wll) return 'WARN';
    return 'OK';
  };

  const addLiftSystem = () => {
    const newLift: LiftSystemInput = {
      liftName: `Lift ${liftSystems.length + 1}`,
      riggingLoadLocation: 'outside',
      topSheaveLocation: 'tower',
      loadPartConfiguration: 2,
      grossLoad: 0,
      loadHeight: 0,
      loadDistance: 0,
      topBlockHeight: 0,
      heelBlockHeight: 0,
      heelBlockDistance: 0,
      hoistDistance: 0,
      tagDistance: 0,
      tagMethod: 'straight',
      accountsForFriction: false,
      blockAttachment: '1-leg',
      faceWidthFt: 0,
      slingLengthFt: 0,
    };
    const updated: LiftSystemInput[] = [...liftSystems, newLift];
    setLiftSystems(updated);
    setEquipmentLists([...equipmentLists, [...equipmentLists[0]]]);
    setCalculatedForces([...calculatedForces, null]);
    setEngineLifts([...engineLifts, null]);
    syncLiftDetailsFromSystems(updated);
  };

  const updateLiftSystem = (index: number, field: keyof LiftSystemInput, value: any) => {
    const updated = [...liftSystems];
    updated[index] = { ...updated[index], [field]: value };
    setLiftSystems(updated);
  };

  const updateContact = (index: number, field: keyof ProjectContact, value: string) => {
    const updated = [...contacts];
    updated[index] = { ...updated[index], [field]: value };
    setContacts(updated);
  };

  const updateEquipment = (liftIndex: number, eqIndex: number, field: keyof EquipmentItem, value: any) => {
    const updated = [...equipmentLists];
    updated[liftIndex][eqIndex] = { ...updated[liftIndex][eqIndex], [field]: value };
    setEquipmentLists(updated);
  };

  // Q8 geometry snapshots pulled from lift inputs (never re-asked). Angles are
  // derived from distances (PRD D9): θ from load offset/height, horizontal tag
  // angle from tag distance/height.
  // F-1: keep lift cards in sync with the add/remove lift flow; every render
  // recomputes via recomputeComputed inside the map (never storage numbers).
  useEffect(() => {
    syncLiftDetailsFromSystems(liftSystems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liftSystems.length, projectInfo.structureType]);

  const liftSnapshots: WizardLiftSnapshot[] = useMemo(
    () =>
      liftSystems.map((lift, i) => {
        const loadLineVertical = Math.max(lift.topBlockHeight - lift.loadHeight, 0.001);
        const theta = Math.atan2(lift.loadDistance, loadLineVertical) * (180 / Math.PI);
        const tagVertical = Math.max(lift.loadHeight, 0.001);
        const tagDeg = Math.atan2(lift.tagDistance, tagVertical) * (180 / Math.PI);
        return {
          name: lift.liftName || `Lift ${i + 1}`,
          grossLoadLbs: lift.grossLoad ?? null,
          loadPositionDeg: Number(theta.toFixed(2)),
          horizontalTagDeg: Number(tagDeg.toFixed(2)),
          attachmentDeltaFt: lift.topBlockHeight > 0 ? Math.max(lift.topBlockHeight - lift.heelBlockHeight, 0) : null,
        };
      }),
    // Audit #2 (HIGH): stable reference across re-renders — this array is the
    // ClassificationWizard's `lifts` prop and an effect dep there; rebuilding
    // it inline every render refired the wizard effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liftSystems]
  );

  // UAT-015: the per-asset inspection snapshot that rides the save payload —
  // one entry per asset-linked equipment row that has a known verdict.
  const buildEquipmentInspections = (): EquipmentInspectionSnapshotEntry[] => {
    const out: EquipmentInspectionSnapshotEntry[] = [];
    const seen = new Set<number>();
    for (const l of liftDetails) {
      for (const eq of l.hardware.equipment) {
        if (eq.assetId == null || seen.has(eq.assetId)) continue;
        const snap = equipmentVerdicts[String(eq.assetId)];
        if (!snap) continue;
        seen.add(eq.assetId);
        out.push({ assetId: eq.assetId, role: eq.role, display: snap.display, reason: snap.reason, at: snap.at });
      }
    }
    return out;
  };

  const buildPlanPayload = (): Record<string, unknown> => ({
    // v1 form shape (keys verbatim) — the B-4 codec upgrades it to PlanDataV2 on read.
    projectInfo,
    contacts,
    structureData,
    duration,
    scopeOfWork,
    meansAndMethods,
    constructionSequencing,
    liftSystems,
    equipmentLists,
    // Audit #1 (CRITICAL): liftDetails IS the v2 editing surface (scenario,
    // DLF, personnelLift, role-tagged equipment, mount/redirect geometry).
    // Without it the read-path upgrader regenerates lifts as S1/S2, DLF 1.0,
    // personnelLift false, null jump/redirect/mount/tieBack/highline. Kept
    // alongside liftSystems (v1-shape compatibility with the server codec);
    // writePlan stamps schemaVersion 2 when liftDetails are present.
    liftDetails: liftDetails.filter((l): l is EngineLift => l !== null).map((l) => ({ ...l, computed: null })),
    // C-2: classification block (validated server-side by validateStoredClassification).
    classification: classification
      ? {
          ...classification,
          exception: {
            ...classification.exception,
            invoked: classification.exception.invoked,
            conditions: exceptionConditions,
            conditionsMeta: exceptionConditionsMeta,
          },
        }
      : null,
    // C-4: compliance steps + proof-test log. Audit #8 (MEDIUM): the persisted
    // steps merge the auto-built §6 output (buildComplianceSteps — previously
    // display-only) with the ExceptionCard-injected step(s), deduped by ref —
    // the PDF reads plan.complianceSteps and must carry the full checklist.
    complianceSteps: buildMergedComplianceSteps(
      classification,
      liftDetails.filter((l): l is EngineLift => l !== null),
      jhaReference,
      complianceSteps
    ),
    proofTests,
    // C-5: personnel / stakeholders / JHA linkage (§1.6 rules 2-3)
    personnel,
    stakeholders,
    jhaReference,
    // Form consolidation: §4.4.5.1 plan-considerations checklist (checked item ids)
    ...(planConsiderations.length > 0 ? { planConsiderations } : {}),
    // WD-T6 / v1.4 #1 — the plan ANCHOR TRUCK (capstan winch anchor) + its
    // anchor-math inputs, snapshot at save time (point-in-time plan record).
    anchorVehicle: anchorVehicle ?? null,
    // UAT-015: point-in-time inspection snapshot for every asset-linked
    // equipment row — the saved plan keeps what the picker knew at save.
    ...(buildEquipmentInspections().length > 0
      ? { equipmentInspections: buildEquipmentInspections() }
      : {}),
    // F-5 audit round 1 (MEDIUM): `attachments` is deliberately OMITTED here.
    // The upload/delete routes are the ONLY writers of planData.attachments —
    // updatePlan replaces planData wholesale, so serializing this tab's mirror
    // would let a stale tab clobber another tab's entries (orphaned bytes).
    // The mirror is display-only, rebuilt from every save/upload/delete
    // response and from readPlan on load.
  });

  const handleSave = async () => {
    if (!linkedProjectId) {
      setToast({ kind: 'error', message: 'Select the linked project before saving.' });
      return;
    }
    if (!projectInfo.projectName.trim()) {
      setToast({ kind: 'error', message: 'Project Name is required before saving.' });
      return;
    }

    setSaving(true);
    try {
      const title = `${projectInfo.projectName.trim()} — Construction Plan`;
      // Audit #1: writePlan stamps schemaVersion 2 when v2 liftDetails ride
      // the payload (the write contract per planDataCodec.writePlan).
      const payload = writePlan(buildPlanPayload());
      const saved = savedPlanId
        ? await updatePlan(savedPlanId, { planData: payload, title })
        : await createPlan({
            projectId: linkedProjectId,
            title,
            description: scopeOfWork || null,
            planData: payload,
          });
      setSavedPlanId(saved.id);
      setPlanSignedAt(saved.signedAt ?? null);
      setToast({
        kind: 'success',
        message: `Construction plan saved${saved.version > 1 ? ` (version ${saved.version})` : ''}.`,
      });
    } catch (error) {
      setToast({
        kind: 'error',
        message: planApiErrorMessage(error, 'Failed to save construction plan.'),
      });
    } finally {
      setSaving(false);
    }
  };

  const [exportingPdf, setExportingPdf] = useState(false);

  // F-4: real client-side PDF export (jspdf + html2canvas) — replaces the
  // alert stub. Diagrams captured from the live LiftDiagramPanel SVGs.
  const handleExportPDF = async () => {
    setExportingPdf(true);
    try {
      await exportPlanPdf({
        // F-4 audit round 1 (CRITICAL): recompute the fresh engine forces at
        // export time — readPlan's v1→v2 upgrader attaches an emptyComputed()
        // cache (status 'ok', all forces 0), which must never reach the PDF.
        // exportPlanPdf re-enforces this too; this call keeps the sections
        // built here and the doc model honest by construction.
        plan: recomputePlanForExport(readPlan({ planData: writePlan(buildPlanPayload()) })),
        title: `${projectInfo.projectName.trim()} — Construction Plan`,
      });
      setToast({ kind: 'success', message: 'PDF exported.' });
    } catch {
      setToast({ kind: 'error', message: 'PDF export failed — try again.' });
    } finally {
      setExportingPdf(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="h-7 w-7 text-blue-600" />
                  {classification
                    ? `CLASS ${classification.selected} CONSTRUCTION PLAN`
                    : 'UNCLASSIFIED (legacy) CONSTRUCTION PLAN'}
                  {classification?.override && (
                    <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full align-middle">
                      OVERRIDE
                    </span>
                  )}
                  {classification?.exception.invoked && (
                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full align-middle">
                      §4.4.4.1 EXCEPTION
                    </span>
                  )}
                </h1>
                <p className="text-sm text-gray-600">Per ANSI/ASSP A10.48-2023</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleExportPDF}
                disabled={exportingPdf}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 disabled:opacity-50"
              >
                <Download className="h-5 w-5" />
                Export PDF
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-plan-top"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 font-semibold disabled:opacity-50"
              >
                <Save className="h-5 w-5" />
                {saving ? 'Saving...' : 'Save Plan'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Toast (B-3: success/error replaces alert) */}
        {toast && (
          <div
            className={`fixed bottom-6 right-6 z-50 max-w-md px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.kind === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}
            role="status"
          >
            {toast.message}
          </div>
        )}
        {/* PROJECT INFORMATION */}
        <Section id="project-info" title="Project Information" icon={Building2} activeSection={activeSection} onToggle={setActiveSection}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Linked Project *</label>
            <select
              value={linkedProjectId ?? ''}
              onChange={(e) => setLinkedProjectId(e.target.value ? Number(e.target.value) : null)}
              className="w-full md:max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— Select a project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.siteAddress})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Plans are stored against a project record. Create one under Projects if it isn't listed.
            </p>
            {structureInfoNote && (
              <p className="text-xs text-gray-600 mt-2 bg-blue-50 border border-blue-100 rounded px-2 py-1" data-testid="project-structure-info-note">
                Structure info (from project): {structureInfoNote}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
              <input
                type="text"
                value={projectInfo.projectName}
                onChange={(e) => setProjectInfo({ ...projectInfo, projectName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Skynet Tower Installation"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project No.</label>
              <input
                type="text"
                value={projectInfo.projectNumber}
                onChange={(e) => setProjectInfo({ ...projectInfo, projectNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="A4A1805A"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={projectInfo.date}
                onChange={(e) => setProjectInfo({ ...projectInfo, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Completed By</label>
              <input
                type="text"
                value={projectInfo.completedBy}
                onChange={(e) => setProjectInfo({ ...projectInfo, completedBy: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
              <input
                type="text"
                value={projectInfo.latitude}
                onChange={(e) => setProjectInfo({ ...projectInfo, latitude: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="0.00000° N"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
              <input
                type="text"
                value={projectInfo.longitude}
                onChange={(e) => setProjectInfo({ ...projectInfo, longitude: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="0.00000° W"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
              <input
                type="text"
                value={projectInfo.customer}
                onChange={(e) => setProjectInfo({ ...projectInfo, customer: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Site Address</label>
              <input
                type="text"
                value={projectInfo.siteAddress}
                onChange={(e) => setProjectInfo({ ...projectInfo, siteAddress: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Structure Owner</label>
              <input
                type="text"
                value={projectInfo.structureOwner}
                onChange={(e) => setProjectInfo({ ...projectInfo, structureOwner: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
              <input
                type="text"
                value={projectInfo.siteName}
                onChange={(e) => setProjectInfo({ ...projectInfo, siteName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site ID No.</label>
              <input
                type="text"
                value={projectInfo.siteIdNumber}
                onChange={(e) => setProjectInfo({ ...projectInfo, siteIdNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Structure Type *</label>
              <select
                value={projectInfo.structureType}
                onChange={(e) => setProjectInfo({ ...projectInfo, structureType: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="guyed">Guyed Tower</option>
                <option value="self-support">Self-Support Tower</option>
                <option value="monopole">Monopole</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Mast pipe / rooster head is NOT a structure type — it's an attachment method. Select your actual
                structure above, then choose "Mast pipe / rooster head" in the Construction Classification Q5
                (rigging attached to a mount or drop-in gin pole). It's additive: rooster-head lifts on any of the
                three structure types are supported.
              </p>
            </div>
          </div>
        </Section>

        {/* PROJECT CONTACTS */}
        <Section id="contacts" title="Project Contacts" icon={Users} activeSection={activeSection} onToggle={setActiveSection}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Role</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Company</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Phone</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact, index) => (
                  <tr key={index} className="border-t border-gray-200">
                    <td className="px-4 py-2 font-medium text-gray-900">{contact.role}</td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) => updateContact(index, 'name', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={contact.company}
                        onChange={(e) => updateContact(index, 'company', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={contact.phone}
                        onChange={(e) => updateContact(index, 'phone', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* PERSONNEL & STAKEHOLDERS (C-5) — auto-populated from crew data; QE = engaged reviewer only */}
        <Section id="personnel" title="Project Personnel & Stakeholders" icon={Users} activeSection={activeSection} onToggle={setActiveSection}>
          <PersonnelPanel
            crewMembers={crewMembers as CrewMember[]}
            personnel={personnel}
            onPersonnelChange={setPersonnel}
            stakeholders={stakeholders}
            onStakeholdersChange={setStakeholders}
            jhaReference={jhaReference}
            onJhaReferenceChange={setJhaReference}
            jhaOptions={jhaOptions}
          />
        </Section>

        {/* STRUCTURE DATA */}
        <Section id="structure" title="Structure Data" icon={Building2} activeSection={activeSection} onToggle={setActiveSection}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Structure Type</label>
              <input
                type="text"
                value={structureData.structureType}
                onChange={(e) => setStructureData({ ...structureData, structureType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Height (ft)</label>
              <NumInput
                value={structureData.height}
                onCommit={(n) => setStructureData({ ...structureData, height: n})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
              <input
                type="text"
                value={structureData.manufacturer}
                onChange={(e) => setStructureData({ ...structureData, manufacturer: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Face Width at Base (ft)</label>
              <NumInput
                value={structureData.faceWidthAtBase}
                onCommit={(n) => setStructureData({ ...structureData, faceWidthAtBase: n})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Length (ft)</label>
              <NumInput
                value={structureData.typicalSectionLength}
                onCommit={(n) => setStructureData({ ...structureData, typicalSectionLength: n})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </Section>

        {/* CONSTRUCTION DURATION */}
        <Section id="duration" title="Construction Duration" icon={Calendar} activeSection={activeSection} onToggle={setActiveSection}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobilization Date</label>
              <input
                type="date"
                value={duration.mobilizationDate}
                onChange={(e) => setDuration({ ...duration, mobilizationDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Demobilization Date</label>
              <input
                type="date"
                value={duration.demobilizationDate}
                onChange={(e) => setDuration({ ...duration, demobilizationDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Wind className="h-4 w-4" /> Max Operational Wind (mph)
              </label>
              <NumInput
                value={duration.maxOperationalWindSpeed}
                onCommit={(n) => setDuration({ ...duration, maxOperationalWindSpeed: n})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Wind className="h-4 w-4" /> Max Non-Operational Wind (mph)
              </label>
              <NumInput
                value={duration.maxNonOperationalWindSpeed}
                onCommit={(n) => setDuration({ ...duration, maxNonOperationalWindSpeed: n})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </Section>

        {/* MEANS & METHODS / SCOPE */}
        <Section id="scope" title="Scope of Work & Means/Methods" icon={Wrench} activeSection={activeSection} onToggle={setActiveSection}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Means & Methods</label>
              <textarea
                value={meansAndMethods}
                onChange={(e) => setMeansAndMethods(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={3}
                placeholder="Describe the rigging methods, equipment setup, and approach..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scope of Work</label>
              <textarea
                value={scopeOfWork}
                onChange={(e) => setScopeOfWork(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={3}
                placeholder="Describe the work to be performed..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Construction Sequencing</label>
              <textarea
                value={constructionSequencing}
                onChange={(e) => setConstructionSequencing(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={3}
                placeholder="Describe the sequence of operations..."
              />
            </div>
          </div>
        </Section>

        {/* CLASSIFICATION (C-2) — guided Q1-Q10, above Lift Systems */}
        <Section id="classification" title="Construction Classification (A10.48-2023 §4.4)" icon={CheckCircle} activeSection={activeSection} onToggle={setActiveSection}>
          <ClassificationWizard
            answers={classifierAnswers}
            onAnswersChange={setClassifierAnswers}
            lifts={liftSnapshots}
            result={classification}
            onResultChange={setClassification}
            qpName={projectInfo.completedBy}
          />
          {/* C-4: §4.4.4.1 exception cards — condition state feeds Q6 in the classifier */}
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-600">Exception path:</label>
              <select
                value={exceptionCardPath}
                onChange={(e) => setExceptionCardPath(e.target.value as ExceptionPath)}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="4.4.4.1(b)">§4.4.4.1(b) — lightweight hoisting away from primary structure</option>
                <option value="4.4.4.1(a)">§4.4.4.1(a) — component modifications</option>
              </select>
            </div>
            <ExceptionCard
              path={exceptionCardPath}
              conditions={exceptionConditions}
              conditionsMeta={exceptionConditionsMeta}
              qpName={projectInfo.completedBy}
              grossLoadLbs={liftSnapshots.length ? Math.max(...liftSnapshots.map((l) => l.grossLoadLbs ?? 0)) : null}
              attachmentDeltaFt={
                liftSnapshots.length
                  ? Math.min(...liftSnapshots.map((l) => l.attachmentDeltaFt ?? Infinity).filter((d) => Number.isFinite(d)))
                  : null
              }
              forceVisible={classifierAnswers.q5_attachmentLocation === 'other' || exceptionCardPath === '4.4.4.1(a)'}
              onConditionsChange={(conditions, meta) => {
                setExceptionConditions(conditions);
                setExceptionConditionsMeta(meta);
                setClassifierAnswers((prev) => ({ ...prev, q6_exceptionConditions: conditions }));
              }}
              complianceSteps={complianceSteps}
              onComplianceStepsChange={setComplianceSteps}
            />
          </div>
          {/* F-3: §6 compliance layer — auto steps + §8.1 banner + §10.2 note */}
          <div className="mt-6 border-t pt-4">
            <ComplianceSteps
              classification={classification}
              liftDetails={liftDetails}
              jhaReference={jhaReference}
              anyPersonnelLift={liftDetails.some((l) => l.personnelLift)}
            />
          </div>
          {/* C-4b: §9.6 proof-test log — one record per (rigging scenario, location) */}
          <div className="mt-6 border-t pt-4">
            <ProofTestLog
              records={proofTests}
              onRecordsChange={setProofTests}
              planId={savedPlanId}
              qpName={projectInfo.completedBy}
              inspectors={crewMembers.map((m) => ({
                id: m.id,
                name: `${m.firstName} ${m.lastName}`.trim(),
                role: m.role,
              }))}
              scenarios={liftSystems.map((lift, i) => ({
                id: `lift-${i + 1}`,
                label: lift.liftName || `Lift ${i + 1}`,
              }))}
              onUploadError={(message) => setToast({ kind: 'error', message })}
            />
          </div>
          {/* F-5: supporting documents — crane lift plan (§8.3), structural/mount
              analyses (§8.7 pattern) — via the existing B-2 multer attachment route */}
          <div className="mt-6 border-t pt-4">
            <AttachmentSlot
              attachments={attachments}
              onChange={setAttachments}
              planId={savedPlanId}
              canEdit={!planSignedAt}
              onError={(message) => setToast({ kind: 'error', message })}
            />
          </div>
        </Section>

        {/* F-1: LIFT DETAIL CARDS — v2 accordion; computed recomputed on every render */}
        {/* §8.8 #8: plan-header Equipment source (truck/trailer/crew) multi-select —
            scopes the lift-detail picker to gear on those containers. */}
        <div className="mt-6 border-t pt-4" data-testid="equipment-source-picker">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            Equipment source (§8.8): gear assigned to the selected truck / trailer / crew
            shows first in each lift's equipment picker.
          </p>
          <div className="flex flex-wrap gap-2">
            {CONTAINER_TYPES.map((c) => (
              <label key={c.value} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={equipmentContainers.includes(c.value)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...equipmentContainers, c.value]
                      : equipmentContainers.filter((x) => x !== c.value);
                    setEquipmentContainers(next);
                    // §8.8 Qwen fix 4: container scope change may reflect a
                    // different kit — drop cached verdicts so cards refetch.
                    invalidateEquipmentCache();
                  }}
                  data-testid={`container-${c.value.toLowerCase()}`}
                  className="rounded"
                />
                {c.label}
              </label>
            ))}
          </div>
        </div>
        {/* WD-T6 / v1.4 #1 — plan ANCHOR TRUCK picker (specific-container):
            the capstan winch anchors to this truck; each lift's rigging
            details show the truck-anchor utilization math. */}
        <div className="mt-4 border rounded-lg p-3 bg-slate-50" data-testid="anchor-truck-picker">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            Anchor truck (v1.4): the capstan winch anchors to this truck — the lift's rigging details
            check the hoisted weight against the truck's weight capacity.
          </p>
          {vehicleLoadState === 'loading' && (
            <p className="text-xs text-gray-500">Loading vehicle assets...</p>
          )}
          {vehicleLoadState === 'failed' && (
            <p className="text-xs text-amber-700">
              Could not load vehicle assets. Refresh the page and try again; without the list the anchor
              truck cannot be selected and the truck-anchor check stays incomplete.
            </p>
          )}
          {vehicleLoadState === 'loaded' && (
            <>
              {vehicleAssets.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No vehicles found yet — create the truck in the Asset Tracker (type &quot;Vehicle&quot;) or in the
                  Fleet module, then pick it here so the plan can run the anchor check.
                </p>
              ) : (
                <select
                  aria-label="Anchor truck"
                  data-testid="anchor-truck-select"
                  value={anchorVehicle ? anchorVehicleKey(anchorVehicle) : ''}
                  onChange={(e) => {
                    const found = vehicleAssets.find((v) => vehicleRowKey(v) === e.target.value);
                    setAnchorVehicle(
                      found
                        ? found.source === 'FLEET'
                          ? {
                              assetId: null,
                              fleetVehicleId: found.id,
                              source: 'FLEET',
                              name: found.name,
                              weightLbs: toNum(found.weightLbs),
                              anchorCapacityLbs: toNum(found.anchorCapacityLbs),
                            }
                          : {
                              assetId: found.id,
                              source: 'ASSET',
                              name: found.name,
                              weightLbs: toNum(found.weightLbs),
                              anchorCapacityLbs: toNum(found.anchorCapacityLbs),
                            }
                        : null
                    );
                  }}
                  className="w-full max-w-md px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                >
                  <option value="">— No anchor truck selected —</option>
                  {vehicleAssets.map((v) => (
                    <option key={vehicleRowKey(v)} value={vehicleRowKey(v)}>
                      {v.name}{v.source === 'FLEET' ? ' (Fleet)' : ''}
                    </option>
                  ))}
                </select>
              )}
              {anchorVehicle && anchorVehicle.weightLbs == null && anchorVehicle.anchorCapacityLbs == null && (
                <p className="text-xs text-amber-700 mt-2" data-testid="anchor-truck-no-weights">
                  {anchorVehicle.name} has no weight data — add Vehicle Weight (or an explicit anchor
                  capacity) on the asset so the anchor math can run.
                </p>
              )}
            </>
          )}
        </div>
        {liftDetails.map((detail, i) => {
          const fresh = recomputeComputed(detail);
          return (
            <LiftDetailCard
              key={detail.id}
              lift={detail}
              index={i}
              computed={fresh}
              onChange={(next) => updateLiftDetail(i, next)}
              canRemove={liftDetails.length > 1}
              containers={equipmentContainers}
              anchorVehicle={anchorVehicle}
              onVerdictsLoaded={handleVerdictsLoaded}
              onRemove={() => {
                setLiftDetails(liftDetails.filter((_, j) => j !== i));
                setLiftSystems(liftSystems.filter((_, j) => j !== i));
                setCalculatedForces(calculatedForces.filter((_, j) => j !== i));
                setEngineLifts(engineLifts.filter((_, j) => j !== i));
              }}
            />
          );
        })}
        {/* Audit #9 (LOW): the hidden vestigial sync button was removed —
            syncLiftDetailsFromSystems runs in the [liftSystems.length] effect. */}

        {/* LIFT SYSTEM DETAILS */}
        {liftSystems.map((lift, liftIndex) => (
          <Section 
            key={liftIndex} 
            id={`lift-${liftIndex}`} 
            title={`Lift System Details - ${lift.liftName}`} 
            icon={FileText}
            activeSection={activeSection}
            onToggle={setActiveSection}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Inputs */}
              {/* PLANNED LIFT DETAILS (form consolidation): the Scenario & Geometry
                  questions live ONLY in the Classification wizard above — this
                  section keeps just the lift/loading configuration the CP owns. */}
              <div className="space-y-4">
                <h4 className="font-bold text-gray-900 border-b pb-2">PLANNED LIFT DETAILS</h4>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={`lift-name-${liftIndex}`} className="block text-xs font-medium text-gray-600 mb-1">Lift Name</label>
                    <input
                      id={`lift-name-${liftIndex}`}
                      type="text"
                      value={lift.liftName}
                      onChange={(e) => updateLiftSystem(liftIndex, 'liftName', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor={`rigging-load-location-${liftIndex}`} className="block text-xs font-medium text-gray-600 mb-1">Rigging Load Location</label>
                    <select
                      id={`rigging-load-location-${liftIndex}`}
                      value={lift.riggingLoadLocation}
                      onChange={(e) => updateLiftSystem(liftIndex, 'riggingLoadLocation', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    >
                      <option value="outside">Outside Tower</option>
                      <option value="inside">Inside Tower</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor={`load-part-config-${liftIndex}`} className="block text-xs font-medium text-gray-600 mb-1">Load Part Configuration</label>
                    <select
                      id={`load-part-config-${liftIndex}`}
                      value={lift.loadPartConfiguration}
                      onChange={(e) => updateLiftSystem(liftIndex, 'loadPartConfiguration', parseInt(e.target.value))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    >
                      <option value={1}>1-Part</option>
                      <option value={2}>2-Part</option>
                    </select>
                    {lift.loadPartConfiguration === 2 && (
                      <p className="text-xs text-amber-700 mt-1" data-testid={`two-part-warning-${liftIndex}`}>
                        2-part reeving halves the winch pull but does NOT increase safe lifting capacity — rope,
                        capstan, blocks, and the tower attachment are each still governed by their own WLL.
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor={`block-attachment-${liftIndex}`} className="block text-xs font-medium text-gray-600 mb-1">Block Attachment (how the block hangs)</label>
                    <select
                      id={`block-attachment-${liftIndex}`}
                      value={lift.blockAttachment}
                      onChange={(e) => updateLiftSystem(liftIndex, 'blockAttachment', e.target.value as '1-leg' | 'faceBasket2')}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    >
                      <option value="1-leg">Single sling — block on one leg at a panel point</option>
                      <option value="faceBasket2">Two slings — face basket, block mid-face on 2 legs at panel points</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Face basket is shown in its own inset diagram beside the tower view — the tower
                      diagram keeps a single un-split load line.
                    </p>
                  </div>
                  {lift.blockAttachment === 'faceBasket2' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Face Width at Attachment Elevation (ft)</label>
                        <NumInput
                          value={lift.faceWidthFt}
                          onCommit={(n) => updateLiftSystem(liftIndex, 'faceWidthFt', n)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Distance between the two legs on the working face, measured at the elevation
                          where the slings attach (face tapers with height on guyed/self-support).
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Sling Length (ft, each)</label>
                        <NumInput
                          value={lift.slingLengthFt}
                          onCommit={(n) => updateLiftSystem(liftIndex, 'slingLengthFt', n)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          α is DERIVED — never typed: α = asin(face width / (2 × sling length)).
                          Refused above 60° from vertical; warning above 45°.
                        </p>
                      </div>
                    </>
                  )}
                  <div>
                    <label htmlFor={`gross-load-${liftIndex}`} className="block text-xs font-medium text-gray-600 mb-1">Gross Load - W (lbs)</label>
                    <NumInput
                      id={`gross-load-${liftIndex}`}
                      value={lift.grossLoad}
                      onCommit={(n) => updateLiftSystem(liftIndex, 'grossLoad', n)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                  </div>
                  <div>
                    <label htmlFor={`dlf-${liftIndex}`} className="block text-xs font-medium text-gray-600 mb-1">
                      Dynamic Load Factor
                    </label>
                    <input
                      id={`dlf-${liftIndex}`}
                      type="number"
                      inputMode="decimal"
                      step="0.05"
                      min={1}
                      max={2}
                      value={liftDetails[liftIndex]?.loading.dynamicFactor ?? 1.0}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (Number.isNaN(v)) return;
                        const clamped = Math.min(2, Math.max(1, v));
                        const d = liftDetails[liftIndex];
                        if (d) {
                          updateLiftDetail(liftIndex, { ...d, loading: { ...d.loading, dynamicFactor: clamped } });
                        }
                      }}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">1.0–2.0. Applied to the gross load in the engine.</p>
                  </div>
                  <div className="col-span-2 text-xs text-gray-500">
                    Structure type/height, top block height, load height, tag method and tag distance are
                    answered once in the Classification wizard (Scenario &amp; Geometry) — the engine derives
                    the geometry from those inputs.
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id={`accounts-friction-${liftIndex}`}
                      type="checkbox"
                      checked={lift.accountsForFriction}
                      onChange={(e) => updateLiftSystem(liftIndex, 'accountsForFriction', e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor={`accounts-friction-${liftIndex}`} className="text-xs font-medium text-gray-600">Accounts for Friction?</label>
                  </div>
                </div>

                <button
                  onClick={() => calculateLiftForces(liftIndex)}
                  className="w-full py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition"
                >
                  Calculate Forces
                </button>

                {/* Calculated Results — v2 engine; refused ⇒ red-hatched ShownWork, never numbers.
                    Form consolidation: plain-English summary first, math behind a Show-math toggle. */}
                {calculatedForces[liftIndex] && calculatedForces[liftIndex]!.status === 'refused' && (
                  <div className="bg-white rounded-lg p-4">
                    <h5 className="font-bold text-red-900 mb-3">REFUSED — NO NUMERIC FORCES</h5>
                    <ShownWork working={calculatedForces[liftIndex]!.working} refusals={calculatedForces[liftIndex]!.refusals} status="refused" />
                  </div>
                )}
                {calculatedForces[liftIndex] && calculatedForces[liftIndex]!.status === 'ok' && (() => {
                  const c = calculatedForces[liftIndex]!;
                  const d = c.derivedAngles;
                  const f = c.forces;
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid={`forces-summary-${liftIndex}`}>
                      <h5 className="font-bold text-blue-900 mb-3">FORCES</h5>
                      {/* Plain-English summary lines first */}
                      <div className="space-y-1 text-sm">
                        <div>Force on top block: <span className="font-bold text-blue-700">{f.topBlockResultantLbs.toFixed(1)} lb</span></div>
                        <div>Hoist pull: <span className="font-bold text-blue-700">{f.winchPullLbs.toFixed(1)} lb</span></div>
                        <div>Tension per sling leg: <span className="font-bold text-blue-700">{(f.linePartTensionLbs * (lift.loadPartConfiguration || 1)).toFixed(1)} lb</span></div>
                        <div>Tag line hold: <span className="font-bold text-blue-700">{f.tagLineLbs != null ? `${f.tagLineLbs.toFixed(1)} lb` : '—'}</span></div>
                      </div>
                      {/* Working/formula tiles behind a Show math toggle (closed by default) */}
                      <button
                        type="button"
                        onClick={() => setShowMath((prev) => ({ ...prev, [liftIndex]: !prev[liftIndex] }))}
                        className="mt-3 text-sm text-blue-700 underline"
                        aria-expanded={!!showMath[liftIndex]}
                      >
                        {showMath[liftIndex] ? 'Hide math' : 'Show math'}
                      </button>
                      {showMath[liftIndex] && (
                        <div className="mt-3 border-t pt-3 grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-gray-600">Load Position Angle (θ):</span> <span className="font-semibold">{d.loadPositionDeg.toFixed(2)}°</span></div>
                          <div><span className="text-gray-600">Tag Angle:</span> <span className="font-semibold">{d.tagAngleDeg != null ? `${d.tagAngleDeg.toFixed(2)}°` : '—'}</span></div>
                          <div><span className="text-gray-600">Fleet Angle:</span> <span className="font-semibold">{d.fleetAngleDeg != null ? `${d.fleetAngleDeg.toFixed(2)}°` : '—'}</span></div>
                          <div><span className="text-gray-600">Sling Angle (α, from vertical):</span> <span className="font-semibold">{d.slingAlphaDeg != null ? `${d.slingAlphaDeg.toFixed(2)}°` : '—'}</span></div>
                          <div className="col-span-2 border-t pt-2 mt-2">
                            <div><span className="text-gray-600">Load Line (per part):</span> <span className="font-bold text-blue-700">{f.linePartTensionLbs.toFixed(1)} lbs</span></div>
                            <div><span className="text-gray-600">Tag Line Force:</span> <span className="font-bold text-blue-700">{f.tagLineLbs != null ? `${f.tagLineLbs.toFixed(1)} lbs` : '—'}</span></div>
                            <div><span className="text-gray-600">Top Block Resultant:</span> <span className="font-bold text-blue-700">{f.topBlockResultantLbs.toFixed(1)} lbs</span></div>
                            <div><span className="text-gray-600">Winch Pull:</span> <span className="font-bold text-blue-700">{f.winchPullLbs.toFixed(1)} lbs</span></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Right: Lift Diagram (v2 — true-scale, scene graph; E-5) */}
              <div>
                <h4 className="font-bold text-gray-900 border-b pb-2 mb-4">LIFT DIAGRAM</h4>
                {engineLifts[liftIndex] && calculatedForces[liftIndex] ? (
                  <>
                    <LiftDiagramPanel
                      lift={engineLifts[liftIndex]!}
                      computed={calculatedForces[liftIndex]!}
                    />
                    {(lift.blockAttachment === '1-leg' ||
                      (lift.blockAttachment === 'faceBasket2' && lift.faceWidthFt > 0 && lift.slingLengthFt > 0)) && (
                      <div className="mt-4">
                        <BlockAttachmentInset
                          blockForceLbs={calculatedForces[liftIndex]!.forces.topBlockResultantLbs}
                          thetaDeg={calculatedForces[liftIndex]!.derivedAngles.loadPositionDeg}
                          config={lift.blockAttachment}
                          faceWidthFt={lift.faceWidthFt}
                          slingLengthFt={lift.slingLengthFt}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">
                    Calculate forces to render the lift diagram.
                  </p>
                )}
              </div>
            </div>

            {/* Equipment Summary Table */}
            <div className="mt-8">
              <h4 className="font-bold text-gray-900 border-b pb-2 mb-4">EQUIPMENT SUMMARY</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-3 py-2 text-left font-semibold">Equipment Type</th>
                      <th className="px-3 py-2 text-left font-semibold">Size</th>
                      <th className="px-3 py-2 text-left font-semibold">Type/Config</th>
                      <th className="px-3 py-2 text-right font-semibold">Listed WLL</th>
                      <th className="px-3 py-2 text-right font-semibold">Required WLL</th>
                      <th className="px-3 py-2 text-right font-semibold">% Utilization</th>
                      <th className="px-3 py-2 text-center font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipmentLists[liftIndex]?.map((eq, eqIndex) => {
                      const util = getUtilizationStatus(eq.requiredWLL, eq.reducedWLL);
                      // Form consolidation: computed-force-vs-WLL status badge. When the
                      // row has no WLL data, show the typical default as an advisory hint
                      // (never overrides user data — the tag is the source of truth).
                      const typicalWll =
                        eq.equipmentType.includes('Capstan') || eq.equipmentType.includes('Hoist')
                          ? 1000
                          : eq.equipmentType.includes('Load Line') || eq.equipmentType.includes('Tag Line')
                            ? 1000
                            : null;
                      const wllForCheck = eq.reducedWLL || typicalWll;
                      const check = wllForCheck
                        ? getWllStatus(eq.requiredWLL, wllForCheck)
                        : null;
                      return (
                        <tr key={eq.id} className="border-t border-gray-200">
                          <td className="px-3 py-2 font-medium">{eq.equipmentType}</td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={eq.size}
                              onChange={(e) => updateEquipment(liftIndex, eqIndex, 'size', e.target.value)}
                              className="w-20 px-1 py-0.5 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-3 py-2 text-gray-600">{eq.type} {eq.config && `/ ${eq.config}`}</td>
                          <td className="px-3 py-2 text-right">
                            <NumInput
                              value={eq.listedWLL}
                              onCommit={(n) => updateEquipment(liftIndex, eqIndex, 'listedWLL', n)}
                              className="w-20 px-1 py-0.5 border border-gray-300 rounded text-sm text-right"
                              />
                          </td>
                          <td className="px-3 py-2 text-right font-medium">{eq.requiredWLL.toFixed(0)} lbs</td>
                          <td className={`px-3 py-2 text-right font-bold ${util.color}`}>
                            {util.percent > 0 ? `${util.percent.toFixed(1)}%` : '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {check ? (
                              <span
                                data-testid={`wll-status-${liftIndex}-${eq.id}`}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${
                                  check === 'FAIL'
                                    ? 'bg-red-100 text-red-700'
                                    : check === 'WARN'
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-green-100 text-green-700'
                                }`}
                              >
                                {check === 'FAIL' && <XCircle className="h-3.5 w-3.5" />}
                                {check === 'WARN' && <AlertTriangle className="h-3.5 w-3.5" />}
                                {check === 'OK' && <CheckCircle className="h-3.5 w-3.5" />}
                                {check}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                            {wllForCheck === typicalWll && eq.reducedWLL === 0 && (
                              <p className="text-xs text-amber-600 mt-0.5">typical — verify from tag</p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Warnings */}
            {calculatedForces[liftIndex] && equipmentLists[liftIndex]?.some(eq => getUtilizationStatus(eq.requiredWLL, eq.reducedWLL).percent > 75) && (
              <div className="mt-4 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-yellow-800">CRITICAL OPERATION NOTICE</p>
                    <p className="text-sm text-yellow-700">
                      This lift shall be classified as a <strong>Critical Operation</strong> due to load demand(s) exceeding 
                      75% of rigging component(s) reduced WLL as defined in ANSI/ASSP A10.48-2023, Section 13.7(a)
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Section>
        ))}

        <button
          onClick={addLiftSystem}
          className="w-full py-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition flex items-center justify-center gap-2 mb-4"
        >
          <Plus className="h-5 w-5" />
          Add Another Lift Configuration
        </button>

        {/* PLAN CONSIDERATIONS (§4.4.5.1 a–n) — collapsible checklist, closed by default */}
        <div className="border border-gray-200 rounded-lg mb-4">
          <button
            type="button"
            onClick={() => setShowPlanConsiderations((prev) => !prev)}
            className="w-full px-6 py-4 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition"
            aria-expanded={showPlanConsiderations}
          >
            <span className="font-semibold text-gray-900">Plan considerations (§4.4.5.1)</span>
            {showPlanConsiderations ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
          </button>
          {showPlanConsiderations && (
            <div className="p-6 bg-white">
              <p className="text-xs text-gray-500 mb-3">
                A10.48-2023 §4.4.5.1 requires the plan to address these considerations — tick each one
                the plan covers. Ticks are saved with the plan.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {PLAN_CONSIDERATION_ITEMS.map((item) => (
                  <label key={item.id} className="flex items-start gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={planConsiderations.includes(item.id)}
                      onChange={(e) =>
                        setPlanConsiderations((prev) =>
                          e.target.checked ? [...prev, item.id] : prev.filter((id) => id !== item.id)
                        )
                      }
                      className="mt-0.5"
                    />
                    {item.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* FINAL SIGN-OFFS */}
        <Section id="signoffs" title="Final Sign-Offs" icon={FileText} activeSection={activeSection} onToggle={setActiveSection}>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-gray-700 italic">
              I hereby certify this construction plan and identified construction means and methods have been developed 
              by the Competent Person and/or Qualified Person identified below in accordance with <strong>ANSI/ASSP A10.48-2023</strong>, 
              and the listed Competent Person shall be present onsite during all construction activities and shall communicate 
              all details of the plan to the construction crew to ensure a safe working environment.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-gray-300 rounded-lg p-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Competent Rigger</label>
              <div className="space-y-2">
                <input type="text" placeholder="Name" className="w-full px-3 py-2 border border-gray-300 rounded" />
                <div className="h-20 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400">
                  Signature
                </div>
                <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded" />
              </div>
            </div>
            <div className="border border-gray-300 rounded-lg p-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Qualified Person</label>
              <div className="space-y-2">
                <input type="text" placeholder="Name" className="w-full px-3 py-2 border border-gray-300 rounded" />
                <div className="h-20 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400">
                  Signature
                </div>
                <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded" />
              </div>
            </div>
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs text-amber-800">
              Competent Person/Qualified Person has reviewed inputs/calculations as presented in this construction plan and 
              its application to the site conditions. Competent Person/Qualified Person accepts full liability of the development 
              of Construction Plan and implementation thereof.
            </p>
          </div>
        </Section>

        {/* Save/Export Footer — UAT-013: the ANSI/ASSP A10.48-2023 compliance
            claim is STATE-AWARE. A plan only earns the "Compliant" label when
            it is classified, all compliance steps are resolved, AND it is
            signed off; anything else shows an honest draft/pending banner
            that says exactly what is missing and how to fix it. */}
        <div className="mt-8 bg-white rounded-lg shadow-lg p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="plan-compliance-status">
          {(() => {
            const compliance = planComplianceStatus({
              classification,
              signed: !!planSignedAt,
              steps: buildMergedComplianceSteps(classification, liftDetails.filter((l): l is EngineLift => l !== null), jhaReference, complianceSteps),
            });
            return compliance.state === 'compliant' ? (
              <div className="text-sm text-gray-600">
                <strong>ANSI/ASSP A10.48-2023</strong> Compliant Construction Plan
              </div>
            ) : (
              <div className="text-sm text-amber-800">
                <strong>{compliance.label}.</strong> {compliance.detail}
              </div>
            );
          })()}
          <div className="flex gap-3">
            <button
              onClick={handleExportPDF}
              disabled={exportingPdf}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 font-semibold disabled:opacity-50"
            >
              <Download className="h-5 w-5" />
              Export PDF
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 font-semibold disabled:opacity-50"
            >
              <Save className="h-5 w-5" />
              {saving ? 'Saving...' : 'Save Construction Plan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
